/**
 * AATOZZ — bespoke one-page landing (Layer-3 tenant override of
 * `@app/storefront/tenant-landing`), a port of the approved handoff
 * (`client design handoffs/design_handoff_aatozz/AATOZZ Storefront.dc.html`):
 * sticky header with anchor nav, cart and Login; hero; welcome; products;
 * membership kits; values; how it works; member stories; closing band; footer;
 * cart drawer.
 *
 * Nothing on it is made up at build time:
 *   - products are the LIVE catalog (featured first) and the kits are the
 *     admin's Landing Packages, so prices, PV and photos are the admin's;
 *   - member stories are the CMS testimonials and the section hides until
 *     there is one (the handoff's sample quotes and review counts are not real);
 *   - every line of copy, the photos and the contact details resolve through
 *     `useSiteContent(AATOZZ_SITE)` (Admin → Storefront → Site Pages).
 *
 * Shop, product, cart, checkout, login and sign-up are the shared pages, themed
 * by `TENANT_PRESETS.aatozz`. Presentation only, no business logic.
 */
/* eslint-disable react-refresh/only-export-components --
 * Two of the four seam exports are values, not components, so this file can
 * never satisfy the rule (see the flx and agp seams). Fast-refresh granularity
 * is the only cost. */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Clock,
  Coffee,
  Facebook,
  Flame,
  Globe,
  HeartHandshake,
  Instagram,
  Leaf,
  Mail,
  MapPin,
  Menu,
  Phone,
  Plus,
  Quote,
  ShieldCheck,
  ShoppingCart,
  TrendingUp,
  Twitter,
  Users,
  Youtube,
  type LucideIcon,
} from 'lucide-react';
import { AATOZZ_SITE } from '@mlm/shared';
import { storefrontAssetUrl } from '../../../lib/api';
import { useAuthStore } from '../../../lib/auth-store';
import { useCartStore, cartCount } from '../../../storefront/lib/cart-store';
import {
  displaySellerName,
  useBrand,
  useCatalog,
  useLandingPackages,
  useProductHref,
  useStorefrontContent,
} from '../../../storefront/lib/hooks';
import { renderRichParas } from '../../../storefront/lib/rich-paras';
import { useSiteContent } from '../../../storefront/lib/site-content';
import type { DisplayProduct } from '../../../storefront/lib/types';
import { useBrandLogo } from '../../../storefront/components/storefront-logo';
import { WishlistHeart } from '../../../storefront/components/wishlist-heart';
import { AzCartDrawer } from './cart-drawer';
import { FALLBACK_SOCIAL, descriptionLines, initials, peso, pesoDigits } from './format';
import './aatozz.css';

export const HAS_BESPOKE_LANDING = true;

/**
 * AATOZZ is a single-landing tenant: no bespoke non-home pages. These two are
 * part of the seam and NOT optional — a tenant folder that omits them fails
 * its build at the `@app/storefront/tenant-landing` import.
 */
export function renderTenantPage(_path: string): JSX.Element | null {
  return null;
}
export const TENANT_ROUTES: string[] = [];

/** Icons are code, paired BY INDEX with the manifest's fixed-length lists. */
const WELCOME_ICONS: LucideIcon[] = [Coffee, Users, TrendingUp];
const VALUE_ICONS: LucideIcon[] = [ShieldCheck, Flame, HeartHandshake, Leaf];

const SOCIAL_ICONS: Record<string, LucideIcon> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  x: Twitter,
  youtube: Youtube,
};

/** A campaign landing owns every click on its product (see product-card.tsx). */
const isCampaignHref = (href: string) => href.startsWith('/r/') || href.startsWith('/c/');

const scrollToId = (id: string) => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (id === 'top') {
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    return;
  }
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
};

export function TenantLanding(): JSX.Element {
  const sc = useSiteContent(AATOZZ_SITE);
  const t = (path: string) => sc.text(`az-home.${path}`);
  const navigate = useNavigate();
  const brand = useBrand();
  const hrefFor = useProductHref();
  const addToCart = useCartStore((s) => s.add);
  const stories = useStorefrontContent().data.company.testimonials ?? [];
  const { products: catalog } = useCatalog();
  const packages = useLandingPackages().products;
  const [cartOpen, setCartOpen] = useState(false);
  const closeCart = useCallback(() => setCartOpen(false), []);

  // The first three products that are not kits (kits have their own section),
  // featured first, then cheapest.
  const featured = useMemo(
    () =>
      catalog
        .filter((p) => !p.isKit)
        .sort((a, b) => Number(!!b.featured) - Number(!!a.featured) || a.price - b.price)
        .slice(0, 3),
    [catalog],
  );

  // Single-SKU products go straight into the cart (the shop card's quick add);
  // a product with variations, or one a campaign page owns, opens its page.
  function choose(p: DisplayProduct) {
    const href = hrefFor(p);
    if (p.variants.length > 0 || isCampaignHref(href)) {
      navigate(href);
      return;
    }
    addToCart({
      productId: p.id,
      name: p.name,
      price: p.price,
      was: p.was || p.price,
      priceGc: p.priceGc,
      priceCoin: p.priceCoin,
      image: p.image,
      variation: '',
      variantId: undefined,
      pv: p.pv,
      cashback: p.cashback ?? 0,
      isVirtual: p.isVirtual,
      sellerId: p.sellerId,
      sellerName: displaySellerName(p.sellerId, p.sellerName, brand),
      qty: 1,
    });
    setCartOpen(true);
  }

  const toPackages = () => (packages.length > 0 ? scrollToId('packages') : navigate('/signup'));
  const toProducts = () => (featured.length > 0 ? scrollToId('products') : navigate('/storefront'));

  const titleLines = t('hero.title').split('\n');
  const credit = t('hero.credit');

  return (
    <div className="az-site">
      <AzHeader
        hasProducts={featured.length > 0}
        hasPackages={packages.length > 0}
        onCart={() => setCartOpen(true)}
      />

      <main>
        <section className="az-hero" id="top">
          <img className="az-hero-img" src={sc.img('az-home.hero.image')} alt="" />
          <div className="az-hero-tint" aria-hidden />
          <div className="az-hero-inner">
            <div className="az-hero-copy">
              {t('hero.pill') && <span className="az-pill">{t('hero.pill')}</span>}
              <h1>
                {titleLines.map((line, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {line}
                  </span>
                ))}
              </h1>
              <p className="az-hero-body">{t('hero.body')}</p>
              <div className="az-hero-ctas">
                <button type="button" className="az-btn az-btn--white" onClick={toPackages}>
                  {t('hero.primaryCta')} <ArrowRight size={17} strokeWidth={2.4} />
                </button>
                <button type="button" className="az-btn az-btn--ghost" onClick={toProducts}>
                  {t('hero.secondaryCta')}
                </button>
              </div>
            </div>
          </div>
          {credit && <span className="az-hero-credit">{credit}</span>}
        </section>

        <section className="az-section">
          <div className="az-welcome">
            <h2 className="az-h2">{t('welcome.title')}</h2>
            <div className="az-welcome-text">
              {renderRichParas(sc.paras('az-home.welcome.paras'))}
            </div>
            <div className="az-welcome-cards">
              {sc.items('az-home.welcome.cards').map((c, i) => {
                const Icon = WELCOME_ICONS[i] ?? Coffee;
                return (
                  <div key={i} className="az-welcome-card">
                    <span className="az-ring" aria-hidden>
                      <Icon size={28} />
                    </span>
                    <h3 className="az-card-title">{c.title}</h3>
                    <p className="az-card-text">{c.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {featured.length > 0 && (
          <section className="az-section az-section--cream" id="products">
            <div className="az-container">
              <div className="az-head">
                <h2 className="az-h2">{t('products.title')}</h2>
                <p className="az-sub">{t('products.subtitle')}</p>
              </div>
              <div className="az-products">
                {featured.map((p) => (
                  <AzProductCard key={p.id} p={p} href={hrefFor(p)} onAdd={() => choose(p)} />
                ))}
              </div>
              <div className="az-view-all">
                <Link className="az-btn az-btn--outline" to="/storefront">
                  {t('products.viewAll')}
                </Link>
              </div>
            </div>
          </section>
        )}

        {packages.length > 0 && (
          <section className="az-section" id="packages">
            <div className="az-container">
              <div className="az-head">
                <h2 className="az-h2">{t('packages.title')}</h2>
                <p className="az-sub">{t('packages.subtitle')}</p>
              </div>
              <div className="az-kits">
                {packages.map((p) => (
                  <AzKitCard
                    key={p.id}
                    p={p}
                    cta={t('packages.cta')}
                    badge={t('packages.popular')}
                    onChoose={() => choose(p)}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="az-section az-section--foam" id="about">
          <div className="az-container" style={{ maxWidth: 1100, textAlign: 'center' }}>
            <h2 className="az-h2">{t('values.title')}</h2>
            <p className="az-sub" style={{ color: 'var(--az-body)', marginBottom: 40 }}>
              {t('values.subtitle')}
            </p>
            <div className="az-values">
              {sc.items('az-home.values.items').map((v, i) => {
                const Icon = VALUE_ICONS[i] ?? Leaf;
                return (
                  <div key={i} className="az-value">
                    <span className="az-dot" aria-hidden>
                      <Icon size={24} />
                    </span>
                    <h3 className="az-value-name">{v.name}</h3>
                    <p className="az-value-text">{v.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="az-section">
          <div className="az-container" style={{ maxWidth: 1100, textAlign: 'center' }}>
            <h2 className="az-h2">{t('steps.title')}</h2>
            <p className="az-sub" style={{ color: 'var(--az-body)', marginBottom: 44 }}>
              {t('steps.subtitle')}
            </p>
            <div className="az-steps">
              {sc.items('az-home.steps.items').map((s, i, all) => (
                <StepWithArrow key={i} last={i === all.length - 1}>
                  <div className="az-step">
                    <div className="az-step-num">{String(i + 1).padStart(2, '0')}</div>
                    <h3 className="az-step-name">{s.name}</h3>
                    <p className="az-step-text">{s.text}</p>
                    <span className="az-step-check" aria-hidden>
                      <Check size={19} strokeWidth={2.6} />
                    </span>
                  </div>
                </StepWithArrow>
              ))}
            </div>
          </div>
        </section>

        {stories.length > 0 && (
          <section className="az-section az-section--stories">
            <div className="az-container" style={{ textAlign: 'center' }}>
              <h2 className="az-h2" style={{ marginBottom: 12 }}>
                {t('stories.title')}
              </h2>
              <p className="az-sub" style={{ marginBottom: 40 }}>
                {t('stories.subtitle')}
              </p>
              <div className="az-stories">
                {stories.map((s, i) => (
                  <figure key={i} className="az-story" style={{ margin: 0 }}>
                    <Quote className="az-story-mark" size={26} fill="currentColor" aria-hidden />
                    <blockquote className="az-story-text" style={{ margin: '0 0 22px' }}>
                      “{s.quote}”
                    </blockquote>
                    <figcaption className="az-story-who">
                      <span className="az-avatar" aria-hidden>
                        {s.avatarKey ? (
                          <img src={storefrontAssetUrl(s.avatarKey)} alt="" />
                        ) : (
                          initials(s.author)
                        )}
                      </span>
                      <span>
                        <span className="az-story-name" style={{ display: 'block' }}>
                          {s.author}
                        </span>
                        {s.role && <span className="az-story-role">{s.role}</span>}
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="az-cta-band">
          <div className="az-cta-band-inner">
            <h2>{t('cta.title')}</h2>
            <p>{t('cta.body')}</p>
            <div className="az-hero-ctas">
              <button type="button" className="az-btn az-btn--white" onClick={toPackages}>
                {t('cta.primaryCta')} <ArrowRight size={17} strokeWidth={2.4} />
              </button>
              <button
                type="button"
                className="az-btn az-btn--ghost"
                onClick={() => scrollToId('contact')}
              >
                {t('cta.secondaryCta')}
              </button>
            </div>
          </div>
        </section>
      </main>

      <AzFooter hasProducts={featured.length > 0} hasPackages={packages.length > 0} />
      <AzCartDrawer open={cartOpen} onClose={closeCart} />
    </div>
  );
}

/** A step plus the arrow that leads to the next one (the handoff's 1fr auto 1fr grid). */
function StepWithArrow({ last, children }: { last: boolean; children: ReactNode }) {
  return (
    <>
      {children}
      {!last && (
        <span className="az-step-arrow" aria-hidden>
          <ArrowRight size={28} />
        </span>
      )}
    </>
  );
}

function useNavItems(hasProducts: boolean, hasPackages: boolean) {
  return [
    { id: 'top', label: 'Home' },
    hasProducts ? { id: 'products', label: 'Our Coffee' } : null,
    { id: 'about', label: 'About' },
    hasPackages ? { id: 'packages', label: 'Membership' } : null,
    { id: 'contact', label: 'Contact' },
  ].filter((n): n is { id: string; label: string } => n !== null);
}

function AzHeader({
  hasProducts,
  hasPackages,
  onCart,
}: {
  hasProducts: boolean;
  hasPackages: boolean;
  onCart: () => void;
}) {
  const { logoUrl, wordmark, brand } = useBrandLogo();
  const authed = !!useAuthStore((s) => s.token);
  const count = useCartStore((s) => cartCount(s.lines));
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavItems(hasProducts, hasPackages);
  const go = (id: string) => {
    setMenuOpen(false);
    scrollToId(id);
  };

  return (
    <header className="az-header">
      <div className="az-header-inner">
        <a
          className="az-brand"
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            go('top');
          }}
        >
          {logoUrl && <img src={logoUrl} alt="" />}
          <span className="az-brand-word">{wordmark || brand.brandName}</span>
        </a>
        <nav className="az-nav" aria-label="Sections">
          {nav.map((n) => (
            <button key={n.id} type="button" className="az-nav-link" onClick={() => go(n.id)}>
              {n.label}
            </button>
          ))}
        </nav>
        <div className="az-header-actions">
          <button
            type="button"
            className="az-icon-btn az-menu-btn"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="az-mobile-nav"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <Menu size={26} />
          </button>
          <button
            type="button"
            className="az-icon-btn"
            aria-label={`Cart, ${count} item${count === 1 ? '' : 's'}`}
            onClick={onCart}
          >
            <ShoppingCart size={26} />
            {count > 0 && <span className="az-cart-badge">{count > 99 ? '99+' : count}</span>}
          </button>
          <Link className="az-login" to={authed ? '/dashboard' : '/login'}>
            {authed ? 'My Account' : 'Login'}
          </Link>
        </div>
      </div>
      <nav id="az-mobile-nav" className="az-mobile-nav" data-open={menuOpen} aria-label="Sections">
        {nav.map((n) => (
          <button key={n.id} type="button" className="az-nav-link" onClick={() => go(n.id)}>
            {n.label}
          </button>
        ))}
      </nav>
    </header>
  );
}

function AzProductCard({ p, href, onAdd }: { p: DisplayProduct; href: string; onAdd: () => void }) {
  const soldOut = p.variants.length === 0 && p.stockQty != null && p.stockQty <= 0;
  return (
    <div className="az-product">
      <div style={{ position: 'relative' }}>
        <Link className="az-product-media" to={href} aria-label={p.name}>
          {p.image ? (
            <img src={p.image} alt="" loading="lazy" />
          ) : (
            <span className="az-placeholder">
              <img src="/tenants/aatozz-logo.png" alt="" />
            </span>
          )}
        </Link>
        <WishlistHeart productId={p.id} className="az-product-fav" noun="product" />
      </div>
      <div className="az-product-body">
        <Link to={href} className="az-product-name">
          {p.name}
        </Link>
        {p.description && <p className="az-product-desc">{p.description}</p>}
        <div className="az-product-foot">
          <div>
            <div className="az-price">
              {peso(p.price)}
              {p.was > p.price && <span className="az-was">{peso(p.was)}</span>}
            </div>
            {p.pv > 0 && <div className="az-pv">{p.pv} PV</div>}
          </div>
          <button type="button" className="az-add" onClick={onAdd} disabled={soldOut}>
            {soldOut ? (
              'Sold out'
            ) : (
              <>
                <Plus size={15} strokeWidth={2.4} />
                {p.variants.length > 0 ? 'Options' : 'Add'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function AzKitCard({
  p,
  cta,
  badge,
  onChoose,
}: {
  p: DisplayProduct;
  cta: string;
  badge: string;
  onChoose: () => void;
}) {
  const popular = !!p.featured;
  const feats = descriptionLines(p.description);
  return (
    <div className={popular ? 'az-kit az-kit--popular' : 'az-kit'}>
      {popular && badge && <span className="az-kit-badge">{badge}</span>}
      <h3 className="az-kit-name">{p.name}</h3>
      <div className="az-kit-price">
        <small>₱</small>
        <span>{pesoDigits(p.price)}</span>
      </div>
      <div className="az-kit-pv">{p.pv > 0 ? `${p.pv} PV · one-time` : 'one-time'}</div>
      <div className="az-kit-rule" />
      <ul className="az-kit-feats">
        {feats.map((f, i) => (
          <li key={i}>
            <Check size={15} strokeWidth={2.6} aria-hidden />
            {f}
          </li>
        ))}
      </ul>
      <button type="button" className="az-kit-cta" onClick={onChoose}>
        {cta}
      </button>
    </div>
  );
}

function AzFooter({ hasProducts, hasPackages }: { hasProducts: boolean; hasPackages: boolean }) {
  const sc = useSiteContent(AATOZZ_SITE);
  const { data } = useStorefrontContent();
  const authed = !!useAuthStore((s) => s.token);
  const nav = useNavItems(hasProducts, hasPackages);
  const f = (k: string) => sc.text(`az-contact.footer.${k}`);
  const d = (k: string) => sc.text(`az-contact.details.${k}`).trim();
  const filled = data.social.filter((s) => s.url);
  const social = filled.length > 0 ? filled : FALLBACK_SOCIAL;

  const contact = [
    { key: 'address', Icon: MapPin, value: d('address') },
    { key: 'hours', Icon: Clock, value: d('hours') },
    { key: 'phone', Icon: Phone, value: d('phone') },
    { key: 'email', Icon: Mail, value: d('email') },
  ].filter((c) => c.value);

  return (
    <footer className="az-footer" id="contact">
      <div className="az-footer-grid">
        <div>
          <span className="az-footer-plate">
            <img src={sc.img('az-contact.footer.logo')} alt="AATOZZ Health & Wellness" />
          </span>
          {f('tagline') && <p className="az-footer-tagline">{f('tagline')}</p>}
          {f('blurb') && <p className="az-footer-blurb">{f('blurb')}</p>}
          <div className="az-social">
            {social.map((s) => {
              const Icon = SOCIAL_ICONS[s.platform.trim().toLowerCase()] ?? Globe;
              return (
                <a
                  key={s.platform + s.url}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={s.platform}
                >
                  <Icon size={17} />
                </a>
              );
            })}
          </div>
        </div>
        <div>
          <h4>Quick Links</h4>
          {nav.map((n) => (
            <button
              key={n.id}
              type="button"
              className="az-footer-link"
              onClick={() => scrollToId(n.id)}
            >
              {n.label}
            </button>
          ))}
        </div>
        <div>
          <h4>Support</h4>
          <Link className="az-footer-link" to={authed ? '/dashboard' : '/login'}>
            Member Portal
          </Link>
          <Link className="az-footer-link" to="/storefront">
            Shop
          </Link>
          <Link className="az-footer-link" to="/faq">
            FAQs
          </Link>
          <Link className="az-footer-link" to="/privacy">
            Privacy Policy
          </Link>
        </div>
        {contact.length > 0 && (
          <div>
            <h4>{f('contactTitle')}</h4>
            {contact.map(({ key, Icon, value }) => (
              <div key={key} className="az-contact-row">
                <Icon size={15} aria-hidden />
                {key === 'email' ? (
                  <a href={`mailto:${value}`}>{value}</a>
                ) : key === 'phone' ? (
                  <a href={`tel:${value.replace(/[^\d+]/g, '')}`}>{value}</a>
                ) : (
                  <span>{value}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="az-footer-bottom">
        <div>
          <span>
            © {new Date().getFullYear()} {f('copyright')}
          </span>
          <span>Powered by Omni Tech Business IT Solutions</span>
        </div>
      </div>
    </footer>
  );
}
